import React, { useCallback } from 'react';
import type { FC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl-next';
import { Query } from 'react-apollo';

import { DropdownItem } from '@atlaskit/dropdown-menu';
import { Box, xcss } from '@atlaskit/primitives';
import { SpotlightManager, SpotlightTarget } from '@atlaskit/onboarding';

import {
	ArchivePagesLoader,
	ArchiveUpsellDialog,
	canUnarchivePage,
	ShowArchiveCTAQuery,
} from '@confluence/archive-pages';
import { DeleteNestedPagesActionLoader } from '@confluence/bulk-page-actions';
import { LoadableLazy } from '@confluence/loadable';
import { pageAnalyticsUrlToRouteUrl } from '@confluence/confluence-analytics/entry-points/routeUtils';
import { CopyPageTreeDialog } from '@confluence/copy-page-tree-dialog';
import { EditorMovePageButtonLoader } from '@confluence/editor-move-page-button';
import { EditLabelDropdownItem } from '@confluence/labels';
import {
	ResolvedInlineCommentsCounterLoader,
	ResolvedInlineCommentsDialogLoader,
} from '@confluence/resolved-inline-comments-dialog';
import { SessionData, useBooleanFeatureFlag } from '@confluence/session-data';
import { AnalyticsPluginWrapper } from '@confluence/plugin-wrappers';
import { ConfluenceEdition } from '@confluence/change-edition';
import {
	ContentOwnerPopup,
	ContentOwnershipContextConsumer,
	CONTENT_OWNERSHIP_WEB_ITEM_ID,
} from '@confluence/content-ownership';
import { ViewChangesDialogLoader } from '@confluence/view-changes-dialog';
import type { DialogsStateContainer } from '@confluence/dialogs';
import { ErrorDisplay } from '@confluence/error-boundary';
import { ANALYTICS_ADDON_ACTIONS_MENU_ID } from '@confluence/web-item';
import type { useWebItemLocationWebItemType } from '@confluence/web-item-location';
import { CUSTOM_CONTENTS_ON_PAGE_LIST } from '@confluence/named-routes';

import { ExpandableSubMenuItem } from './ExpandableSubMenuItem';
import type { ExpandableSubMenuItemItemType } from './ExpandableSubMenuItem';
import { PageAppsExpandableSubMenuItems } from './PageAppsExpandableSubMenuItems';
import { livePagesi18n } from './i18n';
import { i18n } from './EllipsisDropdown';

type ViewChangesItemProps = {
	contentId: string;
	dialogs: DialogsStateContainer;
	saveDraft?: () => void;
};

type AttachmentsMenuItemProps = {
	contentId: string;
	spaceKey: string;
	webItems: useWebItemLocationWebItemType[];
	isSubMenuOpen: boolean;
	handleMouseEnter: () => void;
	handleClick: () => void;
};

const menuItemContainerStyles = xcss({
	display: 'flex',
	justifyContent: 'space-between',
	gap: 'space.200',
});

const resolvedCommentCountStyles = xcss({
	// min width is needed here to prevent layout shift when the count is loaded
	minWidth: '20px',
});
const analyticsSource = 'LivePagesEllipsisDropdownComponent';

const numbersRegex = /\d+/g;

const PublishButtonSpotlightLoader = LoadableLazy({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-PublishButtonSpotlight" */ './LivePagesPublishSpotlight'
			)
		).LivePagesPublishSpotlight,
});

// Publishing will not use publish modal for now.
// See: https://product-fabric.atlassian.net/browse/MODES-3188
export const PublishItem = ({ onClickPublish }) => {
	return (
		<SpotlightManager blanketIsTinted={false}>
			<SpotlightTarget name="live-pages-onboarding.publish-button-in-overflow">
				<DropdownItem
					key="ellipsis-publish-button"
					onClick={() => onClickPublish({ suppressNotifyWatchers: false })()}
				>
					<FormattedMessage {...livePagesi18n.publish} />
				</DropdownItem>
			</SpotlightTarget>
			<PublishButtonSpotlightLoader />
		</SpotlightManager>
	);
};

export const ViewChangesItem: FC<ViewChangesItemProps> = ({ contentId, dialogs, saveDraft }) => {
	const intl = useIntl();

	const onClick = useCallback(() => {
		saveDraft?.();
		dialogs.showModal(ViewChangesDialogLoader, {
			contentId,
			title: intl.formatMessage(livePagesi18n.viewChangesTitle),
		});
	}, [contentId, dialogs, intl, saveDraft]);

	return (
		<DropdownItem key="view-changes" onClick={onClick}>
			<FormattedMessage {...i18n.viewChanges} />
		</DropdownItem>
	);
};

export const EditLabelItem = ({ contentId, spaceKey, dialogs }) => (
	<EditLabelDropdownItem contentId={contentId || ''} spaceKey={spaceKey} dialogs={dialogs} />
);

export const ResolvedCommentsItem = ({ contentId, spaceKey, dialogs }) => (
	<DropdownItem
		key="resolved-comments"
		onClick={() => {
			dialogs.showModal(ResolvedInlineCommentsDialogLoader, {
				spaceKey,
				contentId,
				context: 'editor',
			});
		}}
	>
		<Box xcss={menuItemContainerStyles}>
			<FormattedMessage {...i18n.resolvedComments} />
			<Box xcss={resolvedCommentCountStyles}>
				<ResolvedInlineCommentsCounterLoader contentId={contentId} />
			</Box>
		</Box>
	</DropdownItem>
);

export const MovePageItem = ({ contentId, spaceKey, retrieveEditorTitleAndContent }) => (
	<EditorMovePageButtonLoader
		contentId={contentId}
		spaceKey={spaceKey}
		appearance="dropdownItem"
		analyticsSource={analyticsSource}
		key="move-page-button"
		retrieveEditorTitleAndContent={retrieveEditorTitleAndContent}
		isUnpublishedDraft={false}
	/>
);

export const CopyPageItem = ({ contentId, spaceKey, dialogs }) => (
	<DropdownItem
		key="copy-page"
		onClick={() => {
			dialogs.showModal(CopyPageTreeDialog, {
				contentId,
				spaceKey,
			});
		}}
	>
		<FormattedMessage {...livePagesi18n.copy} />
	</DropdownItem>
);

export const ExportMenuItem = ({ webItems, isSubMenuOpen, handleMouseEnter, handleClick }) => {
	const exportItems = webItems.filter(
		(item) => item.id === 'action-export-pdf-link' || item.id === 'action-export-word-link',
	);

	return (
		<ExpandableSubMenuItem
			label={<FormattedMessage {...livePagesi18n.export} />}
			items={exportItems}
			isSubMenuOpen={isSubMenuOpen}
			onMouseEnter={handleMouseEnter}
			onClick={handleClick}
		/>
	);
};

export const PageHistoryMenuItem = ({ webItems }) => {
	const historyLinkItem = webItems.find((item) => item.id === 'action-view-history-link');

	if (!historyLinkItem) return null;

	return (
		<DropdownItem key="page-history" href={historyLinkItem.url || ''}>
			{historyLinkItem.label}
		</DropdownItem>
	);
};

export const AttachmentsMenuItem: FC<AttachmentsMenuItemProps> = ({
	contentId,
	spaceKey,
	webItems,
	isSubMenuOpen,
	handleMouseEnter,
	handleClick,
}) => {
	const intl = useIntl();

	const attachmentsItem = webItems.find((item) => item.id === 'view-attachments-link') as
		| ExpandableSubMenuItemItemType
		| undefined;

	const items: ExpandableSubMenuItemItemType[] = [];

	if (attachmentsItem) {
		// extract attachments count from label
		const attachmentsCountStr = attachmentsItem?.label?.match(numbersRegex)?.[0] ?? '';
		attachmentsItem.badgeLabel = attachmentsCountStr;
		// relabel attachments to files
		attachmentsItem.label = intl.formatMessage(livePagesi18n.files);
		items.push(attachmentsItem);
		const sectionItemForCustomContents: ExpandableSubMenuItemItemType = {
			id: 'view-custom-contents-link',
			label: intl.formatMessage(livePagesi18n.customContents),
			url: CUSTOM_CONTENTS_ON_PAGE_LIST.toUrl({
				contentId,
				spaceKey,
			}),
		};
		items.push(sectionItemForCustomContents);
	}

	return (
		<ExpandableSubMenuItem
			label={<FormattedMessage {...livePagesi18n.attachments} />}
			items={items}
			isSubMenuOpen={isSubMenuOpen}
			onMouseEnter={handleMouseEnter}
			onClick={handleClick}
		/>
	);
};

export const DeletePageItem = ({ contentId, dialogs, webItems }) => {
	const deleteLinkItem = webItems.find((item) => item.id === 'action-remove-content-link');
	if (!deleteLinkItem) return null;

	const contentType = 'page';

	const handleDeleteClick = () => {
		dialogs.showModal(DeleteNestedPagesActionLoader, {
			contentId,
			source: analyticsSource,
			shouldRedirectOnSuccess: true,
			contentType,
		});
	};

	return (
		<DropdownItem key="delete" onClick={handleDeleteClick}>
			{deleteLinkItem.label}
		</DropdownItem>
	);
};

export const AnalyticsItem = ({ webItems }) => {
	const analyticsLinkItem = webItems.find((item) => item.id === ANALYTICS_ADDON_ACTIONS_MENU_ID);

	if (!analyticsLinkItem) return null;

	// Replace the WebItem URL with the new native UI route URL while keeping the query params
	const url = pageAnalyticsUrlToRouteUrl(analyticsLinkItem.url || '');

	return (
		<AnalyticsPluginWrapper webItemId={analyticsLinkItem.id} key={analyticsLinkItem.completeKey}>
			<DropdownItem href={url} {...analyticsLinkItem}>
				{analyticsLinkItem.label}
			</DropdownItem>
		</AnalyticsPluginWrapper>
	);
};

export const ChangePageOwnershipItem = ({
	webItems,
	contentId,
	closeMenu,
	isOpen,
	onClick,
	onMouseEnter,
	closePopup,
}) => {
	const isContentOwnerSupportedForAllTypes = useBooleanFeatureFlag(
		'confluence.frontend.content.owner.support.all.types',
	);
	const pageOwnerItem = webItems.find((item) => item.id === CONTENT_OWNERSHIP_WEB_ITEM_ID);
	if (!pageOwnerItem) return null;

	return isContentOwnerSupportedForAllTypes ? (
		<ContentOwnerPopup
			contentId={contentId}
			itemId={pageOwnerItem.id}
			itemLabel={pageOwnerItem.label}
			onCloseParent={closeMenu}
			isOpen={isOpen}
			onClick={onClick}
			onMouseEnter={onMouseEnter}
			onClosePopup={closePopup}
		/>
	) : (
		<ContentOwnershipContextConsumer>
			{({ openModal }) => (
				<DropdownItem onClick={openModal} key={pageOwnerItem.completeKey} {...pageOwnerItem}>
					{pageOwnerItem.label}
				</DropdownItem>
			)}
		</ContentOwnershipContextConsumer>
	);
};

export const AdvancedDetailsMenuItem = ({
	webItems,
	isSubMenuOpen,
	handleMouseEnter,
	handleClick,
}) => {
	const ids = [
		'link-to-page-link',
		'view-page-info-link',
		'action-view-source-link',
		'view-in-hierarchy-link',
		'action-view-storage-link',
		'content-api',
		'import-word-doc',
		'end-of-page-recommendation-menu',
	];
	const subItems = webItems.filter((item) => ids.includes(item.id));

	return (
		<ExpandableSubMenuItem
			label={<FormattedMessage {...livePagesi18n.advanced} />}
			items={subItems}
			isSubMenuOpen={isSubMenuOpen}
			onMouseEnter={handleMouseEnter}
			onClick={handleClick}
		/>
	);
};

export const ArchiveLinkMenuItem = ({ webItems, contentId, dialogs }) => {
	const archiveLinkId = 'action-archive-link';
	const archiveLinkItem = webItems.find((item) => item.id === archiveLinkId);
	const handleArchiveMenuClick = (edition: ConfluenceEdition | null) => {
		if (edition !== ConfluenceEdition.FREE) {
			dialogs.showModal(ArchivePagesLoader, {
				contentId,
				source: 'contentToolsItem',
			});
		} else {
			dialogs.showModal(ArchiveUpsellDialog);
		}
	};

	return (
		<SessionData>
			{({ edition }) => (
				<Query<any, any> query={ShowArchiveCTAQuery} variables={{ contentId }}>
					{({ loading, error, data }) => {
						const operations = data?.content?.nodes?.[0]?.operations;
						if (loading) {
							return null;
						} else if (error) {
							return <ErrorDisplay error={error} />;
						} else if (
							edition === ConfluenceEdition.FREE ||
							(operations !== null && canUnarchivePage(operations))
						) {
							return (
								<DropdownItem
									key={archiveLinkItem.completeKey}
									onClick={() => handleArchiveMenuClick(edition)}
								>
									<FormattedMessage {...livePagesi18n.archive} />
								</DropdownItem>
							);
						} else {
							return null;
						}
					}}
				</Query>
			)}
		</SessionData>
	);
};

export const TemplateMenuItem = ({ onClickOpenContentCreationPanel }) => (
	<DropdownItem onClick={onClickOpenContentCreationPanel}>
		<FormattedMessage {...i18n.templateImportDocs} />
	</DropdownItem>
);

export const PageAppsMenuItem = ({
	webItems,
	isSubMenuOpen,
	handleMouseEnter,
	handleClick,
	forgeMenuItems,
}) => (
	<PageAppsExpandableSubMenuItems
		label={<FormattedMessage {...livePagesi18n.pageApps} />}
		items={webItems}
		isSubMenuOpen={isSubMenuOpen}
		onMouseEnter={handleMouseEnter}
		onClick={handleClick}
		forgeMenuItems={forgeMenuItems}
	/>
);
